import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { Observable, forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import Swal from "sweetalert2";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  ResponsabilidadSvCatalogo,
  ResponsabilidadSvCatalogoPayload,
  ResponsabilidadesSvCatalogoService,
} from "../../../../../shared/services/responsabilidades-sv-catalogo.service";
import { CargosService } from "../../../../../shared/services/cargos.service";
import {
  AsignaResponsabilidadCargo,
  AsignaResponsabilidadCargoService,
} from "../../../../../shared/services/asigna-responsabilidad-cargo.service";

interface ResponsabilidadFormValue {
  responsabilidad: string;
  descripcion: string | null;
  cargos: number[];
}

interface ResponsabilidadPorCargo {
  id_cargo: number;
  cargo: string;
  responsabilidades: {
    id: number;
    responsabilidad: string;
    estado: number;
  }[];
}

@Component({
  selector: "app-responsabilidades",
  templateUrl: "./responsabilidades.component.html",
  styleUrls: ["./responsabilidades.component.scss"],
})
export class ResponsabilidadesComponent implements OnInit {
  responsabilidadForm!: FormGroup;
  activeTab = 1;

  guardando = false;
  cargandoListado = false;
  cargandoEdicion = false;
  cargandoCargos = false;

  responsabilidades: ResponsabilidadSvCatalogo[] = [];
  responsabilidadesFiltradas: ResponsabilidadSvCatalogo[] = [];
  cargos: any[] = [];

  filtro = "";
  filtroEstado: number | null = null;
  pageSize = 10;
  page = 1;

  // Estado de edición dentro del mismo formulario
  modoEdicion = false;
  idResponsabilidadEditando: number | null = null;

  // 🔹 Registros actuales de asigna_responsabilidad_cargo de la responsabilidad que se está editando
  // (incluye activos e inactivos, para poder reactivar en vez de duplicar)
  registrosCargoActuales: {
    id: number;
    id_responsabilidad: number;
    id_cargo: number;
    estado: number;
    ResponsabilidadCargoData?: { id: number; cargos: string; estado: number } | null;
  }[] = [];

  responsabilidadesPorCargo: ResponsabilidadPorCargo[] = [];

  readonly Number = Number;

  constructor(
    private fb: FormBuilder,
    private responsabilidadesService: ResponsabilidadesSvCatalogoService,
    private cargosService: CargosService,
    private asignaResponsabilidadCargoService: AsignaResponsabilidadCargoService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarCargos();
    this.cargarResponsabilidades();
  }

  private inicializarFormulario(): void {
    this.responsabilidadForm = this.fb.group({
      responsabilidad: ["", [Validators.required, Validators.maxLength(300)]],
      descripcion: [null],
      cargos: [[], [this.validarArrayNoVacio]],
    });
  }

  private validarArrayNoVacio(
    control: AbstractControl,
  ): ValidationErrors | null {
    const valor = control.value;
    return Array.isArray(valor) && valor.length > 0
      ? null
      : { requerido: true };
  }

  // ============ CATÁLOGOS ============

  private cargarCargos(): void {
    this.cargandoCargos = true;
    this.cargosService
      .getCargosActivos()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.cargos = this.normalizarLista(response);
        this.cargandoCargos = false;
      });
  }

  // ============ LISTADO ============

  cargarResponsabilidades(): void {
    this.cargandoListado = true;

    this.responsabilidadesService.getAll().subscribe({
      next: (response: any) => {
        this.responsabilidades = this.normalizarLista(response);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.responsabilidades = [];
        this.responsabilidadesFiltradas = [];
        this.cargandoListado = false;
      },
    });
  }

  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "")
      .toLowerCase()
      .trim();

    let filtrados = this.responsabilidades.filter((item) => {
      const coincideTexto =
        !termino ||
        [item.responsabilidad]
          .filter((valor) => valor !== null && valor !== undefined)
          .some((valor) => String(valor).toLowerCase().includes(termino));

      const estadoItem = Number(item.estado);
      const coincideEstado =
        this.filtroEstado === null || estadoItem === this.filtroEstado;

      return coincideTexto && coincideEstado;
    });

    filtrados = filtrados.sort((a, b) => {
      const estadoA = Number(a.estado) === 1 ? 0 : 1;
      const estadoB = Number(b.estado) === 1 ? 0 : 1;
      if (estadoA !== estadoB) return estadoA - estadoB;
      return ((a as any).id || 0) - ((b as any).id || 0);
    });

    this.responsabilidadesFiltradas = filtrados;
    this.page = 1;
    this.agruparPorCargo();
  }

  // ============ GUARDAR (CREAR / ACTUALIZAR) ============

  guardar(): void {
    if (this.responsabilidadForm.invalid) {
      this.responsabilidadForm.markAllAsTouched();
      Swal.fire(
        "Campos obligatorios",
        "Completa todos los campos requeridos y selecciona al menos un cargo.",
        "warning",
      );
      return;
    }

    if (this.modoEdicion) {
      this.actualizarResponsabilidad();
      return;
    }

    const formValue = this.responsabilidadForm.getRawValue() as ResponsabilidadFormValue;

    const payload: ResponsabilidadSvCatalogoPayload = {
      responsabilidad: String(formValue.responsabilidad || "")
        .trim()
        .toUpperCase(),
      descripcion: formValue.descripcion
        ? String(formValue.descripcion).trim()
        : null,
      estado: 1,
    };

    this.guardando = true;

    this.responsabilidadesService.create(payload).subscribe({
      next: (response: any) => {
        const nuevoId = response?.data?.id || response?.id;

        if (!nuevoId) {
          this.guardando = false;
          Swal.fire(
            "Error",
            "No se pudo obtener el id de la responsabilidad creada.",
            "error",
          );
          return;
        }

        // 🔹 Todos los cargos seleccionados son nuevos (no hay registros previos)
        this.sincronizarCargos(nuevoId, formValue.cargos, []).subscribe({
          next: () => {
            this.guardando = false;
            Swal.fire("Éxito", "Responsabilidad guardada correctamente.", "success");
            this.resetearFormularioCreacion();
            this.cargarResponsabilidades();
            this.activeTab = 2;
          },
          error: () => {
            this.guardando = false;
            Swal.fire(
              "Atención",
              "La responsabilidad se creó, pero no se pudieron asociar todos los cargos.",
              "warning",
            );
            this.resetearFormularioCreacion();
            this.cargarResponsabilidades();
            this.activeTab = 2;
          },
        });
      },
      error: (error: any) => {
        this.guardando = false;
        const mensaje =
          error?.error?.error || "No se pudo guardar la responsabilidad.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  private actualizarResponsabilidad(): void {
    if (!this.idResponsabilidadEditando) {
      Swal.fire(
        "Error",
        "No hay una responsabilidad seleccionada para editar.",
        "error",
      );
      return;
    }

    const formValue = this.responsabilidadForm.getRawValue() as ResponsabilidadFormValue;

    const payload: Partial<ResponsabilidadSvCatalogoPayload> = {
      responsabilidad: String(formValue.responsabilidad || "")
        .trim()
        .toUpperCase(),
      descripcion: formValue.descripcion
        ? String(formValue.descripcion).trim()
        : null,
    };

    this.guardando = true;

    this.responsabilidadesService
      .update(this.idResponsabilidadEditando, payload)
      .subscribe({
        next: () => {
          this.sincronizarCargos(
            this.idResponsabilidadEditando as number,
            formValue.cargos,
            this.registrosCargoActuales,
          ).subscribe({
            next: () => {
              this.guardando = false;
              Swal.fire(
                "Éxito",
                "Responsabilidad actualizada correctamente.",
                "success",
              );
              this.cancelarEdicion();
              this.cargarResponsabilidades();
              this.activeTab = 2;
            },
            error: () => {
              this.guardando = false;
              Swal.fire(
                "Atención",
                "La responsabilidad se actualizó, pero no se pudieron sincronizar todos los cargos.",
                "warning",
              );
              this.cancelarEdicion();
              this.cargarResponsabilidades();
              this.activeTab = 2;
            },
          });
        },
        error: (error: any) => {
          this.guardando = false;
          const mensaje =
            error?.error?.error || "No se pudo actualizar la responsabilidad.";
          Swal.fire("Error", mensaje, "error");
        },
      });
  }

  // 🔹 Estrategia: crear nuevos, reactivar inactivos, inactivar deseleccionados
  private sincronizarCargos(
    idResponsabilidad: number,
    cargosSeleccionados: number[],
    registrosActuales: AsignaResponsabilidadCargo[],
  ): Observable<any> {
    const seleccionados = new Set((cargosSeleccionados || []).map(Number));

    const registroPorCargo = new Map<number, AsignaResponsabilidadCargo>();
    (registrosActuales || []).forEach((r: any) =>
      registroPorCargo.set(Number(r.id_cargo), r),
    );

    const operaciones: Observable<any>[] = [];

    // Crear (cargo seleccionado sin registro previo) o reactivar (registro inactivo)
    seleccionados.forEach((idCargo) => {
      const registro: any = registroPorCargo.get(idCargo);

      if (!registro) {
        operaciones.push(
          this.asignaResponsabilidadCargoService
            .create({
              id_responsabilidad: idResponsabilidad,
              id_cargo: idCargo,
              estado: 1,
            })
            .pipe(catchError(() => of(null))),
        );
      } else if (Number(registro.estado) !== 1 && registro.id) {
        operaciones.push(
          this.asignaResponsabilidadCargoService
            .update(registro.id, { estado: 1 })
            .pipe(catchError(() => of(null))),
        );
      }
    });

    // Inactivar los que estaban activos y ya no están seleccionados
    registroPorCargo.forEach((registro: any, idCargo) => {
      const estabaActivo = Number(registro.estado) === 1;
      if (estabaActivo && !seleccionados.has(idCargo) && registro.id) {
        operaciones.push(
          this.asignaResponsabilidadCargoService
            .update(registro.id, { estado: 0 })
            .pipe(catchError(() => of(null))),
        );
      }
    });

    return operaciones.length > 0 ? forkJoin(operaciones) : of([]);
  }

  private resetearFormularioCreacion(): void {
    this.responsabilidadForm.reset({
      responsabilidad: "",
      descripcion: null,
      cargos: [],
    });
    this.registrosCargoActuales = [];
  }

  // ============ EDITAR (carga datos en el formulario) ============

  editarResponsabilidad(responsabilidad: ResponsabilidadSvCatalogo): void {
    if (Number((responsabilidad as any)?.estado) !== 1) {
      Swal.fire(
        "Info",
        "Solo se puede editar cuando el registro está activo.",
        "info",
      );
      return;
    }

    const id = Number((responsabilidad as any)?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id de la responsabilidad.", "error");
      return;
    }

    this.cargarYEditarResponsabilidad(id);
  }

  editarResponsabilidadDesdeTabla(
    r: { id: number; responsabilidad: string; estado: number },
    grupo: ResponsabilidadPorCargo,
  ): void {
    if (Number(r.estado) !== 1) {
      Swal.fire(
        "Info",
        "Solo se puede editar cuando el registro está activo.",
        "info",
      );
      return;
    }
    this.cargarYEditarResponsabilidad(r.id);
  }

  private cargarYEditarResponsabilidad(id: number): void {
    this.cargandoEdicion = true;

    this.responsabilidadesService.getById(id).subscribe({
      next: (response: any) => {
        this.cargandoEdicion = false;

        const data = (response as any)?.data ?? response;
        if (!data) {
          Swal.fire(
            "Error",
            "No se pudo cargar la información de la responsabilidad.",
            "error",
          );
          return;
        }

        this.modoEdicion = true;
        this.idResponsabilidadEditando = id;

        this.registrosCargoActuales = data?.AsignaResponsabilidadCargoData || [];

        // Solo los cargos activos van preseleccionados en el ng-select
        const idsActivos = this.registrosCargoActuales
          .filter((r) => Number(r.estado) === 1)
          .map((r) => Number(r.id_cargo));

        this.responsabilidadForm.patchValue({
          responsabilidad: data?.responsabilidad ?? "",
          descripcion: data?.descripcion ?? null,
          cargos: idsActivos,
        });

        this.activeTab = 1;

        Swal.fire({
          icon: "info",
          title: "Editando responsabilidad",
          text: "Los datos se cargaron en el formulario. Modifica lo necesario y presiona Actualizar.",
          timer: 2200,
          showConfirmButton: false,
        });
      },
      error: (error: any) => {
        this.cargandoEdicion = false;
        const mensaje =
          error?.error?.error ||
          "No se pudo cargar la responsabilidad para editar.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idResponsabilidadEditando = null;
    this.resetearFormularioCreacion();
  }

  // ============ CAMBIO DE ESTADO ============

  cambiarEstado(
    responsabilidad: ResponsabilidadSvCatalogo,
    estado: number,
  ): void {
    const id = Number((responsabilidad as any)?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id de la responsabilidad.", "error");
      return;
    }

    const accion = estado === 1 ? "activar" : "inactivar";
    Swal.fire({
      title: `¿Deseas ${accion} esta responsabilidad?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const accionServicio =
        estado === 1
          ? this.responsabilidadesService.activar(id)
          : this.responsabilidadesService.inactivar(id);

      accionServicio.subscribe({
        next: () => {
          Swal.fire(
            "Éxito",
            `Responsabilidad ${accion}da correctamente.`,
            "success",
          );
          this.cargarResponsabilidades();
        },
        error: (error: any) => {
          const mensaje =
            error?.error?.error || `No se pudo ${accion} la responsabilidad.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

  cambiarEstadoResponsabilidad(idResponsabilidad: number, estado: number): void {
    const accion = estado === 1 ? "activar" : "inactivar";
    Swal.fire({
      title: `¿Deseas ${accion} esta responsabilidad?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const accionServicio =
        estado === 1
          ? this.responsabilidadesService.activar(idResponsabilidad)
          : this.responsabilidadesService.inactivar(idResponsabilidad);

      accionServicio.subscribe({
        next: () => {
          Swal.fire(
            "Éxito",
            `Responsabilidad ${accion}da correctamente.`,
            "success",
          );
          this.cargarResponsabilidades();
        },
        error: (error: any) => {
          const mensaje =
            error?.error?.error || `No se pudo ${accion} la responsabilidad.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

  // ============ EXPORTAR ============

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  private agruparPorCargo(): void {
    const mapa = new Map<number, ResponsabilidadPorCargo>();

    this.responsabilidadesFiltradas.forEach((item: any) => {
      const asignaciones = item.AsignaResponsabilidadCargoData || [];

      asignaciones.forEach((asig: any) => {
        const cargoData = asig.ResponsabilidadCargoData;
        if (!cargoData) return;

        const idCargo = Number(cargoData.id);

        if (!mapa.has(idCargo)) {
          mapa.set(idCargo, {
            id_cargo: idCargo,
            cargo: cargoData.cargos,
            responsabilidades: [],
          });
        }

        mapa.get(idCargo)!.responsabilidades.push({
          id: item.id,
          responsabilidad: item.responsabilidad,
          estado: item.estado,
        });
      });
    });

    this.responsabilidadesPorCargo = Array.from(mapa.values()).sort((a, b) =>
      a.cargo.localeCompare(b.cargo),
    );
  }
}